import * as React from 'react';
import {
	imagePageNum,
	imageSrc,
	PdfExtractPage,
	PdfFieldPosition,
	PdfFormField,
	PdfImageMatch,
	PdfTextItem,
} from '@/lib/matchPdfFormFields';
import {
	ClientSignFieldsMap,
	ClientSignFieldValue,
} from './hooks/useClientSignFields';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { isChoicePlaceholder } from './lib/choicePlaceholder';
import {
	FloatingSettingsSuggestion,
	SettingsSuggestionPopover,
} from './SettingsSuggestionPopover';
import {
	blockInvalidContactKey,
	contactFormatError,
	sanitizeContactInput,
} from './lib/contactFieldFormat';
import { ClientSignDataType } from './lib/mockDataTypes';

export interface ClientSignHtmlViewHandle {
	focusField: (fieldId: string) => void;
}

export interface ClientSignHtmlViewProps {
	pages: PdfExtractPage[];
	images: PdfImageMatch[];
	formFields: PdfFormField[];
	fields: ClientSignFieldsMap;
	onFieldValueChange: (fieldId: string, value: ClientSignFieldValue) => void;
	onSignatureClick?: (fieldId: string) => void;
	settingsByFieldId?: Record<string, string>;
	suggestionFieldId?: string | null;
	onApplySuggestion?: (fieldId: string) => void;
	onFieldFocus?: (fieldId: string) => void;
	onFieldBlur?: (fieldId: string) => void;
}

type Box = { x: number; y: number; width: number; height: number };

const FOCUS_RING_CLASS =
	'pointer-events-none absolute -inset-[3px] rounded-md group-focus-within:shadow-[0_0_0_3px_rgba(128,57,223,0.15)]';

const PDF_TEXT_FONT = "font-['Times_New_Roman',Times,serif]";

const fieldBox = (position: PdfFieldPosition, page: PdfExtractPage): Box => {
	const pageWidth = position.pageWidth || page.width || 1;
	const pageHeight = position.pageHeight || page.height || 1;
	const x = (position.screenX ?? position.x) * (page.width / pageWidth);
	const y =
		(position.screenY != null
			? position.screenY
			: position.pageHeight != null
			? position.pageHeight - position.y - position.height
			: position.y) *
		(page.height / pageHeight);
	return {
		x,
		y,
		width: position.width * (page.width / pageWidth),
		height: position.height * (page.height / pageHeight),
	};
};

const containsCenter = (item: PdfTextItem, box: Box): boolean => {
	const centerX = item.x + item.width / 2;
	const centerY = item.y + (item.height || item.fontSize) / 2;
	return (
		centerX >= box.x &&
		centerX <= box.x + box.width &&
		centerY >= box.y &&
		centerY <= box.y + box.height
	);
};

const LINE_GAP = 6;

/** Minimum PDF field width (pt) considered comfortable for text entry inline with a label. */
const PRUDENT_WRITABLE_FIELD_WIDTH_PT = 90;

const shouldStackFieldUnderLabel = (
	widget: FlowField,
	labelParts: Extract<FlowPart, { kind: 'text' }>[],
	pageWidth: number
): boolean => {
	if (
		isInlineChoice(widget.field) ||
		isListChoice(widget.field) ||
		labelParts.length === 0
	) {
		return false;
	}
	const { width: fieldWidth } = widget.box;
	if (fieldWidth < PRUDENT_WRITABLE_FIELD_WIDTH_PT) return true;
	if (fieldWidth < pageWidth * 0.2) return true;
	const labelLength = labelParts.reduce(
		(sum, part) => sum + part.text.trim().length,
		0
	);
	return labelLength > 28 && fieldWidth < pageWidth * 0.35;
};

type FlowField = {
	field: PdfFormField;
	index: number;
	box: Box;
};

type FlowPart =
	| { kind: 'text'; x: number; text: string; fontSize: number }
	| { kind: 'field'; x: number; widget: FlowField };

type FlowRow = { y: number; fontSize: number; parts: FlowPart[] };

const isUnderline = (text: string): boolean =>
	/^[_\s.\-–—]+$/.test(text.trim());

const isInlineChoice = (field: PdfFormField): boolean =>
	field.fieldType === 'PDFCheckBox' ||
	field.fieldType === 'PDFRadioGroup' ||
	field.fieldType === 'PDFRadioButton';

const isListChoice = (field: PdfFormField): boolean =>
	field.fieldType === 'PDFDropdown' || field.fieldType === 'PDFListBox';

const buildRows = (page: PdfExtractPage, widgets: FlowField[]): FlowRow[] => {
	const rows: FlowRow[] = [];
	const place = (y: number, part: FlowPart) => {
		const row = rows.find((item) => Math.abs(item.y - y) <= LINE_GAP);
		if (row) {
			row.parts.push(part);
			if (part.kind === 'text') {
				row.fontSize = Math.max(row.fontSize, part.fontSize);
			}
			return;
		}
		rows.push({
			y,
			fontSize: part.kind === 'text' ? part.fontSize : 12,
			parts: [part],
		});
	};

	page.content.forEach((item) => {
		if (!item.str.trim() || isUnderline(item.str)) return;
		if (widgets.some((widget) => containsCenter(item, widget.box))) return;
		place(item.y, {
			kind: 'text',
			x: item.x,
			text: item.str,
			fontSize: item.fontSize || 12,
		});
	});

	widgets.forEach((widget) => {
		let nearestIndex = -1;
		let nearestDist = 18;
		rows.forEach((row, index) => {
			const dist = Math.abs(row.y - widget.box.y);
			if (dist <= nearestDist) {
				nearestIndex = index;
				nearestDist = dist;
			}
		});
		const part: FlowPart = { kind: 'field', x: widget.box.x, widget };
		if (nearestIndex >= 0) rows[nearestIndex].parts.push(part);
		else place(widget.box.y, part);
	});

	rows.sort((a, b) => a.y - b.y);
	rows.forEach((row) => row.parts.sort((a, b) => a.x - b.x));
	return stackSideBySideFields(rows, page.width || 1);
};

const stackSideBySideFields = (
	rows: FlowRow[],
	pageWidth: number
): FlowRow[] => {
	const stacked: FlowRow[] = [];
	rows.forEach((row) => {
		const fields = row.parts.filter(
			(part): part is Extract<FlowPart, { kind: 'field' }> =>
				part.kind === 'field'
		);
		if (
			fields.length < 2 ||
			fields.every((field) => isInlineChoice(field.widget.field))
		) {
			stacked.push(row);
			return;
		}
		const sorted = [...fields].sort((a, b) => a.x - b.x);
		const spanned =
			sorted.reduce((sum, field) => sum + field.widget.box.width, 0) >
			pageWidth * 0.7;
		const sideBySide =
			spanned ||
			sorted.some(
				(field, index) =>
					index > 0 && field.x - sorted[index - 1].x > pageWidth * 0.15
			);
		if (!sideBySide) {
			stacked.push(row);
			return;
		}
		const nearestField = (x: number) => {
			let best = 0;
			let bestDist = Number.POSITIVE_INFINITY;
			sorted.forEach((field, index) => {
				const box = field.widget.box;
				const dist =
					x < box.x
						? box.x - x
						: x > box.x + box.width
						? x - (box.x + box.width)
						: 0;
				if (dist < bestDist) {
					best = index;
					bestDist = dist;
				}
			});
			return best;
		};
		sorted.forEach((field, index) => {
			const parts = row.parts
				.filter((part) => {
					if (part.kind === 'field') return part.widget === field.widget;
					return nearestField(part.x) === index;
				})
				.sort((a, b) => a.x - b.x);
			stacked.push({
				y: row.y + index * 0.001,
				fontSize: row.fontSize,
				parts,
			});
		});
	});
	return stacked;
};

const dominantFontSize = (rows: FlowRow[]): number => {
	const counts = new Map<number, number>();
	rows.forEach((row) => {
		row.parts.forEach((part) => {
			if (part.kind !== 'text') return;
			const size = Math.round(part.fontSize * 2) / 2;
			counts.set(size, (counts.get(size) ?? 0) + part.text.length);
		});
	});
	let best = 12;
	let bestCount = 0;
	counts.forEach((count, size) => {
		if (count > bestCount) {
			best = size;
			bestCount = count;
		}
	});
	return best;
};

const extractedFieldValue = (field: PdfFormField): ClientSignFieldValue => {
	const raw = field.value;
	if (field.fieldType === 'PDFCheckBox') {
		if (typeof raw === 'boolean') return raw;
		const text = Array.isArray(raw) ? raw.join('') : String(raw ?? '');
		const normalized = text.trim().toLowerCase();
		return normalized !== '' && normalized !== 'off' && normalized !== 'false';
	}
	if (Array.isArray(raw)) return raw.find((item) => item.trim()) ?? '';
	if (typeof raw === 'boolean') return raw ? 'true' : '';
	return raw ?? '';
};

const FocusFrame = ({
	className,
	children,
}: {
	className?: string;
	children: React.ReactNode;
}) => (
	<div
		className={clsx(
			'group relative inline-flex min-w-0 max-w-full align-baseline',
			className
		)}
	>
		{children}
		<span aria-hidden className={FOCUS_RING_CLASS} />
	</div>
);

const HtmlDropdown = ({
	sessionId,
	value,
	options,
	readOnly,
	onChange,
	settingsValue,
	showSuggestion = false,
	onApplySuggestion,
	onFocus,
	onBlur,
}: {
	sessionId: string;
	value: string;
	options: string[];
	readOnly: boolean;
	onChange: (value: string) => void;
	settingsValue?: string;
	showSuggestion?: boolean;
	onApplySuggestion?: () => void;
	onFocus?: () => void;
	onBlur?: () => void;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const suggestionRef = React.useRef<HTMLDivElement>(null);
	const hasCustomValue = value !== '' && !options.includes(value);

	React.useEffect(() => {
		if (!isOpen) return undefined;

		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as Node;
			if (
				containerRef.current?.contains(target) ||
				suggestionRef.current?.contains(target)
			) {
				return;
			}
			setIsOpen(false);
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsOpen(false);
		};

		document.addEventListener('pointerdown', onPointerDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('pointerdown', onPointerDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen]);

	const selectOption = (nextValue: string) => {
		onChange(nextValue);
		setIsOpen(false);
	};

	const suggestionVisible = Boolean(
		(showSuggestion || isOpen) &&
			(value.trim().length === 0 || isChoicePlaceholder(value)) &&
			settingsValue
	);

	return (
		<div
			ref={containerRef}
			className={clsx('w-full', isOpen ? 'z-[80]' : undefined)}
		>
			<FocusFrame className="w-full">
				<button
					type="button"
					data-sign-field={sessionId}
					aria-haspopup="listbox"
					aria-expanded={isOpen}
					disabled={readOnly}
					onClick={() => {
						onFocus?.();
						setIsOpen((open) => !open);
					}}
					onFocus={() => onFocus?.()}
					onBlur={() => onBlur?.()}
					className={clsx(
						'flex w-full min-w-0 items-center border-b border-black bg-transparent px-1 py-0.5 pr-5 text-left text-base outline-none disabled:cursor-not-allowed',
						readOnly ? 'bg-[#0D06160A] text-dark-100' : 'text-black'
					)}
				>
					<span className={clsx('min-w-0 truncate', !value && 'text-gray-35')}>
						{value || 'Select'}
					</span>
				</button>
				<ChevronDown
					className="pointer-events-none absolute right-0.5 top-1/2 size-3.5 -translate-y-1/2 text-dark-100"
					aria-hidden
				/>
				{isOpen && (
					<div
						role="listbox"
						className="absolute left-0 top-full z-[90] mt-0.5 max-h-40 min-w-full overflow-y-auto rounded-md border border-dark-100/10 bg-white py-1 shadow-shadow2"
					>
						{hasCustomValue && (
							<button
								type="button"
								role="option"
								aria-selected
								className="block w-full truncate bg-purple-25 px-2 py-1 text-left text-sm"
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => selectOption(value)}
							>
								{value}
							</button>
						)}
						{options.map((option) => (
							<button
								key={option}
								type="button"
								role="option"
								aria-selected={option === value}
								className={clsx(
									'block w-full truncate px-2 py-1 text-left text-sm hover:bg-purple-25',
									option === value && 'bg-purple-25'
								)}
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => selectOption(option)}
							>
								{option}
							</button>
						))}
					</div>
				)}
				{suggestionVisible && settingsValue && (
					<FloatingSettingsSuggestion
						anchorRef={containerRef}
						popoverRef={suggestionRef}
						settingsValue={settingsValue}
						onApply={() => {
							onApplySuggestion?.();
							setIsOpen(false);
						}}
					/>
				)}
			</FocusFrame>
		</div>
	);
};

const sessionIdForName = (
	fields: ClientSignFieldsMap,
	fieldName: string
): string | undefined =>
	Object.entries(fields).find(
		([, field]) => field.fieldName === fieldName
	)?.[0];

/** Same id scheme as `fieldsFromExtracted` in backendPdfFields.ts */
const sessionIdForWidget = (
	fields: ClientSignFieldsMap,
	pageNum: number,
	fieldName: string,
	widgetIndex: number
): string | undefined => {
	const detectedId = `${pageNum - 1}-${fieldName}-${widgetIndex}`;
	if (fields[detectedId]) return detectedId;
	return sessionIdForName(fields, fieldName);
};

const isSignatureImage = (value: ClientSignFieldValue): value is string =>
	typeof value === 'string' &&
	(value.startsWith('data:') ||
		value.startsWith('http://') ||
		value.startsWith('https://'));

const HtmlControl = ({
	field,
	sessionId,
	value,
	optionValue,
	onChange,
	onSignatureClick,
	readOnly = false,
	settingsValue,
	showSuggestion = false,
	onApplySuggestion,
	onFocus,
	onBlur,
	isSignature = false,
	dataType,
	placeholder = '',
}: {
	field: PdfFormField;
	sessionId: string;
	value: ClientSignFieldValue;
	optionValue?: string;
	onChange: (value: ClientSignFieldValue) => void;
	onSignatureClick?: (fieldId: string) => void;
	readOnly?: boolean;
	settingsValue?: string;
	showSuggestion?: boolean;
	onApplySuggestion?: () => void;
	onFocus?: () => void;
	onBlur?: () => void;
	isSignature?: boolean;
	dataType?: ClientSignDataType;
	placeholder?: string;
}) => {
	const [chromeLocked, setChromeLocked] = React.useState(true);
	const chromeLockTimer = React.useRef<number | null>(null);
	const box = field.positions?.[0];
	const chromeFieldName = `widget-${box?.pageNum ?? 0}-${Math.round(
		box?.x ?? 0
	)}-${Math.round(box?.y ?? 0)}`;

	React.useEffect(
		() => () => {
			if (chromeLockTimer.current !== null) {
				window.clearTimeout(chromeLockTimer.current);
			}
		},
		[]
	);

	const releaseChromeLock = () => {
		if (readOnly) return;
		if (chromeLockTimer.current !== null) {
			window.clearTimeout(chromeLockTimer.current);
			chromeLockTimer.current = null;
		}
		setChromeLocked(false);
	};

	const unlockChromeForKeyboard = (
		event: React.SyntheticEvent<HTMLInputElement>
	) => {
		if (readOnly) return;
		event.currentTarget.readOnly = false;
		releaseChromeLock();
		event.currentTarget.focus();
	};

	const engageChromeLock = () => {
		if (chromeLockTimer.current !== null) {
			window.clearTimeout(chromeLockTimer.current);
			chromeLockTimer.current = null;
		}
		setChromeLocked(true);
	};

	if (
		isSignature ||
		field.fieldType === 'PDFSignature' ||
		field.fieldType === 'signature'
	) {
		return (
			<FocusFrame className="w-full">
				<button
					type="button"
					data-sign-field={sessionId}
					disabled={readOnly}
					onPointerDown={(event) => {
						event.currentTarget.dataset.pressed = '1';
					}}
					onPointerCancel={(event) => {
						delete event.currentTarget.dataset.pressed;
					}}
					onClick={(event) => {
						if (event.currentTarget.dataset.pressed !== '1') return;
						delete event.currentTarget.dataset.pressed;
						onSignatureClick?.(sessionId);
					}}
					className="flex min-h-[4.5rem] w-full items-center justify-start border-b border-black bg-transparent px-1 text-base font-medium text-opacity-black outline-none"
				>
					{isSignatureImage(value) ? (
						<Image
							src={value}
							alt="Signature"
							width={120}
							height={70}
							unoptimized
							className="h-[70px] w-auto max-w-full object-contain"
						/>
					) : (
						'Draw Signature'
					)}
				</button>
			</FocusFrame>
		);
	}

	if (field.fieldType === 'PDFCheckBox') {
		return (
			<FocusFrame className="flex items-center justify-center">
				<input
					type="checkbox"
					data-sign-field={sessionId}
					checked={Boolean(value)}
					disabled={readOnly}
					onChange={(event) => onChange(event.target.checked)}
					className="h-5 w-5 accent-primary outline-none mx-1"
				/>
			</FocusFrame>
		);
	}

	if (field.fieldType === 'PDFDropdown' || field.fieldType === 'PDFListBox') {
		return (
			<HtmlDropdown
				sessionId={sessionId}
				value={typeof value === 'string' ? value : ''}
				options={field.options ?? []}
				readOnly={readOnly}
				onChange={onChange}
				settingsValue={settingsValue}
				showSuggestion={showSuggestion}
				onApplySuggestion={onApplySuggestion}
				onFocus={onFocus}
				onBlur={onBlur}
			/>
		);
	}

	if (
		field.fieldType === 'PDFRadioGroup' ||
		field.fieldType === 'PDFRadioButton'
	) {
		const exportValue = optionValue || field.fieldName;
		return (
			<FocusFrame className="flex items-center justify-center">
				<input
					type="radio"
					name={sessionId}
					data-sign-field={sessionId}
					disabled={readOnly}
					checked={value === exportValue}
					onChange={() => onChange(exportValue)}
					className="h-4 w-4 outline-none mx-1"
				/>
			</FocusFrame>
		);
	}

	const stringValue = typeof value === 'string' ? value : '';
	const formatError = readOnly
		? null
		: contactFormatError(dataType, stringValue);
	const suggestionVisible = Boolean(
		showSuggestion && stringValue.trim().length === 0 && settingsValue
	);

	return (
		<div className={clsx('w-full', suggestionVisible ? 'z-[80]' : undefined)}>
			<FocusFrame className="w-full">
				<input
					type={dataType === 'PHONE' ? 'tel' : 'text'}
					inputMode={dataType === 'PHONE' ? 'numeric' : undefined}
					data-sign-field={sessionId}
					name={chromeFieldName}
					placeholder={placeholder}
					autoComplete="one-time-code"
					aria-invalid={Boolean(formatError)}
					disabled={readOnly}
					readOnly={!readOnly && chromeLocked}
					value={stringValue}
					onTouchStart={unlockChromeForKeyboard}
					onTouchEnd={(event) => event.preventDefault()}
					onMouseDown={unlockChromeForKeyboard}
					onKeyDown={(event) => blockInvalidContactKey(dataType, event)}
					onPaste={(event) => {
						event.preventDefault();
						onChange(
							sanitizeContactInput(
								dataType,
								event.clipboardData.getData('text')
							)
						);
					}}
					onChange={(event) =>
						onChange(sanitizeContactInput(dataType, event.target.value))
					}
					onFocus={() => {
						onFocus?.();
						releaseChromeLock();
					}}
					onBlur={() => {
						engageChromeLock();
						onBlur?.();
					}}
					className={clsx(
						'block w-full border-b border-black bg-transparent px-1 py-0.5 text-base outline-none',
						formatError ? 'text-main-red' : 'text-black'
					)}
				/>
				{suggestionVisible && settingsValue && (
					<SettingsSuggestionPopover
						settingsValue={settingsValue}
						onApply={() => onApplySuggestion?.()}
					/>
				)}
			</FocusFrame>
			{formatError && (
				<p className="mt-0.5 text-[10px] leading-tight text-main-red text-left">
					{formatError}
				</p>
			)}
		</div>
	);
};

export const ClientSignHtmlView = React.forwardRef<
	ClientSignHtmlViewHandle,
	ClientSignHtmlViewProps
>(function ClientSignHtmlView(
	{
		pages,
		images,
		formFields,
		fields,
		onFieldValueChange,
		onSignatureClick,
		settingsByFieldId = {},
		suggestionFieldId = null,
		onApplySuggestion,
		onFieldFocus,
		onFieldBlur,
	},
	ref
) {
	const rootRef = React.useRef<HTMLDivElement>(null);
	const editedFieldIds = React.useRef(new Set<string>());

	React.useEffect(() => {
		Object.entries(fields).forEach(([id, fieldState]) => {
			if (
				fieldState.fieldType === 'signature' &&
				typeof fieldState.value === 'string' &&
				fieldState.value.trim().length > 0
			) {
				editedFieldIds.current.add(id);
			}
		});
	}, [fields]);

	React.useImperativeHandle(
		ref,
		() => ({
			focusField: (fieldId: string) => {
				const node = rootRef.current?.querySelector<HTMLElement>(
					`[data-sign-field="${CSS.escape(fieldId)}"]`
				);
				if (!node) return;
				node.scrollIntoView({ behavior: 'smooth', block: 'center' });
				node.focus({ preventScroll: true });
			},
		}),
		[]
	);

	const write = (fieldName: string, value: ClientSignFieldValue) => {
		const sessionId = sessionIdForName(fields, fieldName);
		if (!sessionId || fields[sessionId]?.readOnly) return;
		editedFieldIds.current.add(sessionId);
		onFieldValueChange(sessionId, value);
	};

	const valueForField = (
		sessionId: string | undefined,
		sessionValue: ClientSignFieldValue | undefined,
		field: PdfFormField
	): ClientSignFieldValue => {
		if (typeof sessionValue === 'string' && isSignatureImage(sessionValue)) {
			return sessionValue;
		}
		if (sessionId && editedFieldIds.current.has(sessionId)) {
			return sessionValue ?? '';
		}
		if (
			typeof sessionValue === 'string' &&
			(sessionValue as string)?.trim().length > 0
		) {
			return sessionValue;
		}
		if (sessionValue === true) return true;
		const extracted = extractedFieldValue(field);
		if (typeof extracted === 'string' && extracted.trim().length > 0) {
			return extracted;
		}
		if (extracted === true) return true;
		return sessionValue ?? '';
	};

	return (
		<div ref={rootRef} className="bg-white px-4 py-6 text-black rounded-lg">
			{pages.map((page, pageIndex) => {
				const pageFields = formFields.flatMap((field) =>
					(field.positions ?? [])
						.filter((position) => position.pageNum === page.pageNum)
						.map((position, index) => ({
							field,
							position,
							index,
							box: fieldBox(position, page),
						}))
				);
				const pageImages = [
					...(page.images ?? []),
					...images.filter((image) => imagePageNum(image) === page.pageNum),
				];

				const rows = buildRows(page, pageFields);
				const bodyFont = dominantFontSize(rows);
				const blocks = [
					...rows.map((row) => ({ kind: 'row' as const, y: row.y, row })),
					...pageImages.map((image) => ({
						kind: 'image' as const,
						y: image.y,
						image,
					})),
				].sort((a, b) => a.y - b.y);

				return (
					<div
						key={page.pageNum}
						className={clsx(
							'bg-white text-black [overflow-wrap:anywhere]',
							pageIndex > 0 && 'mt-8 border-t border-gray-30 pt-8'
						)}
					>
						{blocks.map((block) => {
							if (block.kind === 'image') {
								return (
									<div
										key={`${page.pageNum}-img-${block.image.index}-${block.image.y}`}
										className="relative my-4"
									>
										<Image
											src={imageSrc(block.image.url)}
											alt=""
											width={block.image.width}
											height={block.image.height}
											className="h-auto max-w-full object-contain object-left"
										/>
									</div>
								);
							}
							const role =
								block.row.fontSize >= bodyFont * 1.3
									? 'title'
									: block.row.fontSize >= bodyFont * 1.12
									? 'subtitle'
									: 'body';
							const hasField = block.row.parts.some(
								(part) => part.kind === 'field'
							);
							if (!hasField) {
								return (
									<p
										key={`${page.pageNum}-row-${block.y}`}
										className={clsx(
											PDF_TEXT_FONT,
											'mb-3 flex w-full min-w-0 flex-wrap gap-x-1',
											role === 'title' &&
												'justify-center text-xl font-bold leading-snug',
											role === 'subtitle' &&
												'text-lg font-semibold leading-snug',
											role === 'body' && 'text-base leading-7 text-left'
										)}
									>
										{block.row.parts.map((part, partIndex) =>
											part.kind === 'text' ? (
												<span key={`${block.y}-text-${partIndex}`}>
													{part.text}
												</span>
											) : null
										)}
									</p>
								);
							}
							const groups: {
								before: Extract<FlowPart, { kind: 'text' }>[];
								field: Extract<FlowPart, { kind: 'field' }> | null;
								after: Extract<FlowPart, { kind: 'text' }>[];
							}[] = [];
							const parts = block.row.parts;
							let index = 0;
							while (index < parts.length) {
								const part = parts[index];
								if (part.kind === 'text') {
									const before: Extract<FlowPart, { kind: 'text' }>[] = [];
									while (index < parts.length && parts[index].kind === 'text') {
										const textPart = parts[index];
										if (textPart.kind === 'text') before.push(textPart);
										index += 1;
									}
									const next = parts[index];
									if (next && next.kind === 'field') {
										groups.push({ before, field: next, after: [] });
										index += 1;
									} else {
										groups.push({ before, field: null, after: [] });
									}
									continue;
								}
								index += 1;
								const after: Extract<FlowPart, { kind: 'text' }>[] = [];
								if (isInlineChoice(part.widget.field)) {
									while (index < parts.length && parts[index].kind === 'text') {
										const textPart = parts[index];
										if (textPart.kind === 'text') after.push(textPart);
										index += 1;
									}
								}
								groups.push({ before: [], field: part, after });
							}
							return (
								<div
									key={`${page.pageNum}-row-${block.y}`}
									className="mb-4 flex w-full flex-wrap items-center gap-x-4 gap-y-2"
								>
									{groups.map((group, groupIndex) => {
										const inline = Boolean(
											group.field && isInlineChoice(group.field.widget.field)
										);
										const stackUnderLabel = Boolean(
											group.field &&
												shouldStackFieldUnderLabel(
													group.field.widget,
													group.before,
													page.width || 1
												)
										);
										const labelText = (
											items: Extract<FlowPart, { kind: 'text' }>[]
										) =>
											items.map((part, partIndex) => (
												<span
													key={`${block.y}-label-${groupIndex}-${partIndex}`}
												>
													{part.text}
												</span>
											));
										return (
											<div
												key={`${block.y}-group-${groupIndex}`}
												className={clsx(
													'flex min-w-0 gap-x-2 gap-y-1',
													inline
														? 'w-auto flex-row items-center'
														: stackUnderLabel
														? 'w-full flex-col items-stretch'
														: 'w-full flex-row flex-wrap items-center'
												)}
											>
												{group.before.length > 0 && (
													<span
														className={clsx(
															PDF_TEXT_FONT,
															'text-base font-semibold leading-6 text-left'
														)}
													>
														{labelText(group.before)}
													</span>
												)}
												{group.field &&
													(() => {
														const { field, index } = group.field.widget;
														const sessionId = sessionIdForWidget(
															fields,
															page.pageNum,
															field.fieldName,
															index
														);
														const sessionField = sessionId
															? fields[sessionId]
															: undefined;
														const fieldValue = valueForField(
															sessionId,
															sessionField?.value,
															field
														);
														const isEmpty =
															typeof fieldValue !== 'string' ||
															fieldValue.trim().length === 0 ||
															(isListChoice(field) &&
																isChoicePlaceholder(fieldValue));
														const settingsValue = sessionId
															? settingsByFieldId[sessionId]
															: undefined;
														const showSuggestion = Boolean(
															sessionId &&
																suggestionFieldId === sessionId &&
																isEmpty &&
																settingsValue
														);
														const isSignature =
															sessionField?.fieldType === 'signature' ||
															sessionField?.dataType === 'SIGNATURE' ||
															field.fieldType === 'PDFSignature' ||
															field.fieldType === 'signature';
														return (
															<div
																className={clsx(
																	inline
																		? undefined
																		: stackUnderLabel
																		? 'min-w-0 w-full'
																		: 'min-w-[min(100%,9rem)] flex-1 basis-[9rem]'
																)}
															>
																<HtmlControl
																	key={`${page.pageNum}-${field.fieldName}-${index}`}
																	field={field}
																	isSignature={isSignature}
																	dataType={sessionField?.dataType}
																	placeholder={sessionField?.label}
																	sessionId={
																		sessionId ?? `${field.fieldName}-${index}`
																	}
																	value={fieldValue}
																	optionValue={field.options?.[index]}
																	onChange={(value) =>
																		write(field.fieldName, value)
																	}
																	onSignatureClick={
																		sessionId ? onSignatureClick : undefined
																	}
																	readOnly={
																		!sessionId || sessionField?.readOnly
																	}
																	settingsValue={settingsValue}
																	showSuggestion={showSuggestion}
																	onApplySuggestion={
																		sessionId
																			? () => onApplySuggestion?.(sessionId)
																			: undefined
																	}
																	onFocus={
																		sessionId
																			? () => onFieldFocus?.(sessionId)
																			: undefined
																	}
																	onBlur={
																		sessionId
																			? () => onFieldBlur?.(sessionId)
																			: undefined
																	}
																/>
															</div>
														);
													})()}
												{group.after.length > 0 && (
													<span
														className={clsx(
															PDF_TEXT_FONT,
															'shrink-0 text-base font-semibold leading-6'
														)}
													>
														{labelText(group.after)}
													</span>
												)}
											</div>
										);
									})}
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
});

export default ClientSignHtmlView;
